import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationGstAmountOutput from "../../../../components/calculations/APCalculationGstAmountOutput";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../../components/form/APAutoComplete";
import {
  InclusionCalculationId,
  QuoteInclusionCalculations,
  QuoteInclusonCalculationsMap,
  SellPriceCalculationInput,
  SellPriceProRiskLPIAccidentCalculationInput,
  SellPriceProRiskLPICalculationInput,
} from "../../../../types/Calculation";

type Inputs = {
  inclusion: AutoCompleteOption | null;
};

const InclusionCalculations = ({
  calculations,
}: // displayedFields,
{
  calculations: QuoteInclusonCalculationsMap;
  // displayedFields: DisplayedFields;
}) => {
  const { control, watch } = useForm<Inputs>({ mode: "onTouched" });
  const inclusion = watch("inclusion");
  const selectedInclusion = inclusion && calculations[inclusion.id];
  const inclusionOptions = useMemo(() => {
    return Object.values(calculations).map((calculation) => ({
      id: calculation.option.id,
      label: `${calculation.option.name}${
        !calculation.option.available ? " (Disabled)" : ""
      }`,
    }));
  }, [calculations]);

  const renderInputFields = (
    calculations: QuoteInclusionCalculations<SellPriceCalculationInput>
  ) => {
    switch (calculations.calculationId) {
      case InclusionCalculationId.SellPriceProRiskLPI: {
        const input =
          calculations.inputs as SellPriceProRiskLPICalculationInput;

        return (
          <Box>
            <Box>
              <APCalculation name="State" value={input.state} />

              <APCalculationInput
                name="Onroad Vehicle Price"
                value={input.onroadVehiclePrice}
                currency
              />
            </Box>

            <Box>
              <APCalculationInput
                name="Base Premium Multiplier"
                value={input.basePremiumMultiplier}
              />
              <APCalculationInput
                name="Stamp Duty Rate"
                value={input.stampDutyRate}
              />
              <APCalculationInput
                name="Broker Fee - Ex Gst"
                value={input.brokerFee.exGst}
                currency
              />
            </Box>
          </Box>
        );
      }
      case InclusionCalculationId.SellPriceProRiskLPIAccident: {
        const input =
          calculations.inputs as SellPriceProRiskLPIAccidentCalculationInput;

        return (
          <Box>
            <Box>
              <APCalculation name="State" value={input.state} />

              <APCalculationInput
                name="Onroad Vehicle Price"
                value={input.onroadVehiclePrice}
                currency
              />
            </Box>

            <Box>
              <APCalculationInput
                name="Base Premium Multiplier"
                value={input.basePremiumMultiplier}
              />
              <APCalculationInput
                name="Stamp Duty Rate"
                value={input.stampDutyRate}
              />
              <APCalculationInput
                name="Broker Fee - Ex Gst"
                value={input.brokerFee.exGst}
                currency
              />
            </Box>
          </Box>
        );
      }
    }
  };

  return (
    <Box>
      <APPrintablePage
        topContent={
          <Stack spacing={2}>
            <Box sx={{ width: 300 }}>
              <APAutoComplete<Inputs>
                name="inclusion"
                control={control}
                options={inclusionOptions}
                label="Inclusion"
                validations={{}}
                errors={{}}
                defaultValue={null}
              />
            </Box>

            {selectedInclusion && !selectedInclusion.calculation && (
              <Box>No calculation found for this inclusion</Box>
            )}

            {selectedInclusion?.calculation && (
              <Stack spacing={2}>
                {renderInputFields(selectedInclusion.calculation)}

                {!!selectedInclusion.calculation.basePremium && (
                  <Box>
                    <Typography variant="titleLarge">Base Premium</Typography>
                    <APCalculationGstAmountOutput
                      calculation={selectedInclusion.calculation.basePremium}
                    />
                  </Box>
                )}

                {!!selectedInclusion.calculation.stampDuty && (
                  <Box>
                    <Typography variant="titleLarge">Stamp Duty</Typography>
                    <APCalculationOutput
                      calculation={selectedInclusion.calculation.stampDuty}
                      currency
                    />
                  </Box>
                )}

                <Box>
                  <Typography variant="titleLarge">Sell Price</Typography>
                  <APCalculationGstAmountOutput
                    calculation={selectedInclusion.calculation.sellPrice}
                  />
                </Box>

                <Box>
                  <Typography variant="titleLarge">Revenue</Typography>
                  <APCalculationGstAmountOutput
                    calculation={selectedInclusion.calculation.revenue}
                  />
                </Box>

                <Box>
                  <Typography variant="titleLarge">Buy Price</Typography>
                  <APCalculationGstAmountOutput
                    calculation={selectedInclusion.calculation.buyPrice}
                  />
                </Box>
              </Stack>
            )}

            <Box></Box>
          </Stack>
        }
        bottomContent={
          <Stack spacing={4}>
            <Box></Box>
          </Stack>
        }
      />
    </Box>
  );
};

export default InclusionCalculations;
